import React from 'react';

interface Substitution {
  playerIn: number;
  playerOut: number;
  minute: number;
}

interface GoalEvent {
  playerName: string;
  minute: number;
  assistName?: string;
}

interface BookingEvent {
  playerName: string;
  minute: number;
  type: 'yellow' | 'red';
}

interface Props {
  homeTeamName: string;
  awayTeamName: string;
  homeScore: number;
  awayScore: number;
  dateUtc: string;
  venue: string;
  substitutions: Substitution[];
  playerNames: Map<number, string>;
  goals?: GoalEvent[];
  bookings?: BookingEvent[];
}

const MatchInfoCard: React.FC<Props> = ({
  homeTeamName,
  awayTeamName,
  homeScore,
  awayScore,
  dateUtc,
  venue,
  substitutions,
  playerNames,
  goals,
  bookings,
}) => {
  const formatDate = (d: string) => {
    try {
      return new Date(d.replace(' ', 'T')).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return d;
    }
  };

  const getName = (id: number) => playerNames.get(id) || `#${id}`;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 text-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          {homeTeamName} {homeScore} - {awayScore} {awayTeamName}
        </span>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {formatDate(dateUtc)} &middot; {venue || 'N/A'}
      </div>

      <div className="space-y-3">
        {/* Goals */}
        {goals && goals.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Goals</h5>
            <ul className="space-y-0.5">
              {goals.map((g) => (
                <li key={`${g.playerName}-${g.minute}`} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <span>&#9917;</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">{g.playerName}</span>
                  <span className="text-gray-400">({g.minute}')</span>
                  {g.assistName && (
                    <span className="text-gray-500 dark:text-gray-400 ml-1">
                      assist: <span className="text-blue-600 dark:text-blue-400">{g.assistName}</span>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Bookings */}
        {bookings && bookings.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Bookings</h5>
            <ul className="space-y-0.5">
              {bookings.map((b) => (
                <li key={`${b.playerName}-${b.minute}`} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <span
                    className={`inline-block w-2.5 h-3.5 rounded-[1px] ${b.type === 'yellow' ? 'bg-yellow-400' : 'bg-red-600'}`}
                  />
                  <span className="font-medium text-gray-900 dark:text-gray-100">{b.playerName}</span>
                  <span className="text-gray-400">({b.minute}')</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Substitutions */}
        {substitutions.length > 0 && (
          <div>
            <h5 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Substitutions</h5>
            <ul className="space-y-0.5">
              {substitutions.map((sub) => (
                <li key={`${sub.playerIn}-${sub.playerOut}-${sub.minute}`} className="text-xs text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <span className="text-green-600 dark:text-green-400">&#8593; {getName(sub.playerIn)}</span>
                  <span className="text-red-600 dark:text-red-400">&#8595; {getName(sub.playerOut)}</span>
                  <span className="text-gray-400">({sub.minute}')</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchInfoCard;
